import React, { useState, useReducer } from 'react'
import { CardFrame } from '../../../ui/CardFrame/CardFrame.jsx'
import { PriorityRow } from '../../../ui/PriorityRow/PriorityRow.jsx'
import { CarryBanner } from '../../../ui/PullCarryBanner/PullCarryBanner.jsx'
import { usePriorities } from '../../../hooks/usePriorities.js'

/**
 * NextPrioritiesCard — catalog #5, CORE, session-next-priorities.
 *
 * Ranked list, hard cap 5. Carry banner at the bottom (Phase H carry-forward).
 *
 * Phase H replaces the Phase F note-row stub with typed Priority rows backed
 * by the `listPriorities` / `addPriority` / `updatePriority` / `carryPriority`
 * Lambda operations.
 *
 * Carry mechanic:
 *   - Each non-empty, non-carried row has a "Carry →" button.
 *   - Clicking calls carryPriority(priorityId) → Lambda atomically:
 *       1. Flips old priority status to "carried".
 *       2. Creates a new Priority in the next session with carriedFromId set.
 *       3. Writes a CARRY edge for animation choreography.
 *   - Carry animation: the row plays the carry-forward CSS animation
 *     (carry-forward keyframes in ui.css) unless prefers-reduced-motion is set,
 *     in which case a reduced-motion fade is used instead.
 *   - CarryBanner shows the count of priorities that will carry forward
 *     (i.e. non-empty rows that haven't been carried/dropped yet).
 *
 * Props:
 *   sessionId   — required; ties the hook to the session.
 *   disabled    — session-locked state; hides carry buttons.
 */

const MAX_PRIORITIES = 5

function useCarryAnimation() {
  const [carrying, setCarrying] = useState(null) // priorityId being animated

  function startCarry(priorityId) {
    setCarrying(priorityId)
  }

  function endCarry() {
    setCarrying(null)
  }

  return { carrying, startCarry, endCarry }
}

export function NextPrioritiesCard({ sessionId, disabled = false }) {
  const { priorities, loading, addPriority, updatePriority, carryPriority } = usePriorities(
    sessionId,
    { enabled: !!sessionId },
  )
  const { carrying, startCarry, endCarry } = useCarryAnimation()
  const [carryError, setCarryError] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')
  const [addingRank, setAddingRank] = useState(null)
  const [newText, setNewText] = useState('')

  // Detect prefers-reduced-motion once (hook-level is fine — this value doesn't
  // change during a session; the media query listener is intentionally omitted
  // to keep the component simple).
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  // Slots: 5 pre-allocated. Show existing priorities by rank, rest as empty.
  const slots = Array.from({ length: MAX_PRIORITIES }, (_, i) => {
    const rank = i + 1
    return priorities.find((p) => p.rank === rank) ?? null
  })

  const carriableCount = priorities.filter(
    (p) => p.text && !['carried', 'dropped'].includes(p.status),
  ).length

  async function handleCarry(priorityId) {
    if (disabled) return
    setCarryError(null)
    startCarry(priorityId)
    try {
      await carryPriority(priorityId)
    } catch (err) {
      setCarryError(err?.message ?? 'Carry failed.')
    } finally {
      endCarry()
    }
  }

  async function handleEditCommit(priority) {
    if (!editText.trim()) {
      setEditingId(null)
      return
    }
    try {
      await updatePriority(priority.priorityId, { text: editText.trim() })
    } catch {
      // silently revert on error — the original text persists in state
    } finally {
      setEditingId(null)
    }
  }

  async function handleAddCommit(rank) {
    if (!newText.trim()) {
      setAddingRank(null)
      return
    }
    try {
      await addPriority({ text: newText.trim(), rank })
    } catch {
      // silently fail — server will surface cap errors via the error state
    } finally {
      setAddingRank(null)
      setNewText('')
    }
  }

  const filledCount = priorities.filter((p) => p.text).length
  const hasItems = filledCount > 0
  const state = disabled ? 'locked' : hasItems ? 'filled' : 'empty'

  if (loading) {
    return (
      <CardFrame
        kind="session"
        icon="▲"
        iconClass="clay"
        title="Next priorities"
        prompt="What are the top priorities before the next session?"
        state="empty"
        data-testid="session-card-next-priorities"
        className="session-card"
      >
        <p className="session-card__empty" aria-live="polite">
          Loading…
        </p>
      </CardFrame>
    )
  }

  return (
    <CardFrame
      kind="session"
      icon="▲"
      iconClass="clay"
      title="Next priorities"
      prompt="What are the top priorities before the next session?"
      state={state}
      data-testid="session-card-next-priorities"
      className="session-card"
    >
      {carryError && (
        <p className="session-card__error" role="alert">
          {carryError}
        </p>
      )}

      <ol className="priority-list" aria-label="Next priorities">
        {slots.map((priority, index) => {
          const rank = index + 1
          const isCarrying = carrying === priority?.priorityId
          const isCarried = priority?.status === 'carried'
          const isDropped = priority?.status === 'dropped'
          const canCarry =
            !disabled && priority && priority.text && !isCarried && !isDropped

          // Carry animation class — honours prefers-reduced-motion
          let animClass = ''
          if (isCarrying) {
            animClass = prefersReducedMotion
              ? 'priority-list__row--carry-reduced'
              : 'priority-list__row--carrying'
          } else if (isCarried) {
            animClass = 'priority-list__row--carried'
          }

          return (
            <li key={priority?.priorityId ?? `slot-${rank}`} className="priority-list__slot">
              <div
                className={['priority-list__row', animClass].filter(Boolean).join(' ')}
                data-testid={`priority-slot-${rank}`}
              >
                {priority && editingId === priority.priorityId ? (
                  // Inline edit mode
                  <div className="priority-list__edit">
                    <span className="priorityrow__rank" aria-hidden="true">
                      {rank}
                    </span>
                    <input
                      className="priority-list__edit-input"
                      value={editText}
                      autoFocus
                      aria-label={`Edit priority ${rank}`}
                      maxLength={500}
                      onChange={(e) => setEditText(e.target.value)}
                      onBlur={() => handleEditCommit(priority)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleEditCommit(priority)
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                    />
                  </div>
                ) : priority ? (
                  // Existing priority row
                  <PriorityRow
                    rank={rank}
                    text={
                      isCarried
                        ? `${priority.text} ↗ carried`
                        : isDropped
                          ? `${priority.text} (dropped)`
                          : priority.text
                    }
                    status={priority.status}
                    onClick={
                      !disabled && !isCarried && !isDropped
                        ? () => {
                            setEditingId(priority.priorityId)
                            setEditText(priority.text)
                          }
                        : undefined
                    }
                    aria-label={`Priority ${rank}: ${priority.text}, status ${priority.status}`}
                  />
                ) : addingRank === rank ? (
                  // New entry mode
                  <div className="priority-list__edit">
                    <span className="priorityrow__rank" aria-hidden="true">
                      {rank}
                    </span>
                    <input
                      className="priority-list__edit-input"
                      value={newText}
                      autoFocus
                      aria-label={`New priority ${rank}`}
                      maxLength={500}
                      placeholder={`Priority ${rank}…`}
                      onChange={(e) => setNewText(e.target.value)}
                      onBlur={() => handleAddCommit(rank)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddCommit(rank)
                        if (e.key === 'Escape') {
                          setAddingRank(null)
                          setNewText('')
                        }
                      }}
                    />
                  </div>
                ) : (
                  // Empty slot
                  <PriorityRow
                    rank={rank}
                    empty
                    onClick={
                      !disabled
                        ? () => {
                            setAddingRank(rank)
                            setNewText('')
                          }
                        : undefined
                    }
                    aria-label={`Priority ${rank}: empty${disabled ? '' : ', click to add'}`}
                  />
                )}

                {canCarry && (
                  <button
                    type="button"
                    className="priority-list__carry-btn"
                    aria-label={`Carry priority ${rank} forward to next session`}
                    aria-busy={isCarrying}
                    disabled={isCarrying}
                    onClick={() => handleCarry(priority.priorityId)}
                  >
                    {isCarrying ? '…' : '↗'}
                  </button>
                )}
              </div>
            </li>
          )
        })}
      </ol>

      {/* CarryBanner — Phase H: shows count of priorities that will carry forward */}
      <CarryBanner
        destination="into next session's prep"
        aria-label={
          carriableCount > 0
            ? `${carriableCount} ${carriableCount === 1 ? 'priority' : 'priorities'} will carry forward into next session`
            : 'No priorities to carry forward yet'
        }
      />
    </CardFrame>
  )
}
